import { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { SwalAlert } from '@/hooks/useSweetAlert';
import type { Host, HostStatus } from '@/mocks/adminHosts';
import { getStationHosts, hostStatusLabels, hostStatusColors, hostStatusDotColors } from '@/mocks/adminHosts';
import HostCard from './components/HostCard';

type LoadState = 'loading' | 'ready' | 'error' | 'empty';

export default function AdminHostsPage() {
  const navigate = useNavigate();
  const [dataState, setDataState] = useState<LoadState>('loading');
  const [hosts, setHosts] = useState<Host[]>([]);
  const [search, setSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<HostStatus | 'todos'>('todos');
  const mountedRef = useRef(false);

  const loadData = () => {
    setDataState('loading');
    try {
      const data = getStationHosts(1);
      if (!data || data.length === 0) {
        setDataState('empty');
        setHosts([]);
      } else {
        setHosts(data);
        setDataState('ready');
      }
    } catch {
      setDataState('error');
    }
  };

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      const timer = setTimeout(() => loadData(), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const retry = () => loadData();

  /* Counts */
  const counts = useMemo(() => {
    const onAir = hosts.filter((h) => h.status === 'on_air').length;
    const active = hosts.filter((h) => h.status === 'active').length;
    const inactive = hosts.filter((h) => h.status === 'inactive').length;
    return { total: hosts.length, onAir, active, inactive };
  }, [hosts]);

  const filteredHosts = useMemo(() => {
    return hosts.filter((h) => {
      const matchesSearch =
        h.name.toLowerCase().includes(search.toLowerCase()) ||
        h.username.toLowerCase().includes(search.toLowerCase()) ||
        h.programs.some((p) => p.name.toLowerCase().includes(search.toLowerCase()));
      const matchesStatus = selectedStatus === 'todos' || h.status === selectedStatus;
      return matchesSearch && matchesStatus;
    });
  }, [hosts, search, selectedStatus]);

  const totalFollowers = hosts.reduce((sum, h) => sum + h.followers, 0);
  const totalInteractions = hosts.reduce((sum, h) => sum + h.totalInteractions, 0);
  const totalRevenue = hosts.reduce((sum, h) => sum + h.projectedRevenue, 0);

  /* Handlers */
  const handleViewProfile = (host: Host) => {
    Swal.fire({
      title: host.name,
      html: `
        <div class="text-left space-y-3">
          <div class="flex items-center gap-4">
            <div class="w-16 h-16 rounded-xl bg-background-100 overflow-hidden shrink-0">
              <img src="${host.photoUrl}" alt="${host.name}" class="w-full h-full object-cover" />
            </div>
            <div>
              <p class="text-foreground-950 font-bold text-sm">${host.name}</p>
              <p class="text-foreground-400 text-[11px]">${host.username}</p>
              <span class="inline-flex items-center gap-1.5 mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${hostStatusColors[host.status]}">
                ${host.status === 'on_air' ? '<span class="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>' : `<span class="w-1.5 h-1.5 rounded-full ${hostStatusDotColors[host.status]}"></span>`}
                ${hostStatusLabels[host.status]}
              </span>
            </div>
          </div>
          <p class="text-foreground-600 text-xs leading-relaxed">${host.bio}</p>
          <div class="bg-background-100 rounded-xl p-3 space-y-2">
            <p class="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Programas</p>
            ${host.programs.map((p) => `
              <div class="flex items-center gap-2 text-[11px]">
                <div class="w-1.5 h-1.5 rounded-full bg-secondary-400 shrink-0"></div>
                <span class="text-foreground-700 font-semibold">${p.name}</span>
                <span class="text-foreground-400">${p.schedule}</span>
              </div>
            `).join('')}
          </div>
          <div class="grid grid-cols-3 gap-2">
            <div class="bg-background-100 rounded-lg p-2.5 text-center">
              <p class="text-foreground-950 font-bold text-sm">${host.followers >= 1000 ? `${(host.followers / 1000).toFixed(1)}K` : host.followers}</p>
              <p class="text-foreground-400 text-[9px]">Seguidores</p>
            </div>
            <div class="bg-background-100 rounded-lg p-2.5 text-center">
              <p class="text-foreground-950 font-bold text-sm">${host.totalInteractions >= 1000 ? `${(host.totalInteractions / 1000).toFixed(1)}K` : host.totalInteractions}</p>
              <p class="text-foreground-400 text-[9px]">Interacciones</p>
            </div>
            <div class="bg-background-100 rounded-lg p-2.5 text-center">
              <p class="text-foreground-950 font-bold text-sm">₡${(host.monthlyRevenue / 1000).toFixed(0)}K</p>
              <p class="text-foreground-400 text-[9px]">Ingreso mensual</p>
            </div>
          </div>
          <p class="text-foreground-400 text-[10px]">En RFW desde ${new Date(host.joinedAt).toLocaleDateString('es-CR', { year: 'numeric', month: 'long' })}</p>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: true,
      cancelButtonText: 'Cerrar',
      customClass: {
        popup: 'rounded-2xl border border-background-200/70 max-w-[420px]',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-background-100 text-foreground-600 hover:bg-background-200',
      },
      buttonsStyling: false,
    });
  };

  const handleEdit = async (host: Host) => {
    const result = await Swal.fire({
      title: `Editar: ${host.name}`,
      html: `
        <div class="text-left space-y-3">
          <div class="space-y-1.5">
            <label class="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Nombre</label>
            <input id="swal-host-name" class="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-800 text-xs outline-none focus:border-primary-400" value="${host.name}" />
          </div>
          <div class="space-y-1.5">
            <label class="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Bio</label>
            <textarea id="swal-host-bio" class="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-800 text-xs outline-none focus:border-primary-400 resize-none" rows="3" maxlength="500">${host.bio.replace(/"/g, '&quot;')}</textarea>
          </div>
          <p class="text-foreground-400 text-[9px]">Los cambios se guardan localmente (demo).</p>
        </div>
      `,
      showConfirmButton: true,
      confirmButtonText: 'Guardar cambios',
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      customClass: {
        popup: 'rounded-2xl border border-background-200/70 max-w-[420px]',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-primary-500 text-white hover:bg-primary-600',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-background-100 text-foreground-600 hover:bg-background-200',
      },
      buttonsStyling: false,
      preConfirm: () => {
        const nameEl = document.getElementById('swal-host-name') as HTMLInputElement;
        const bioEl = document.getElementById('swal-host-bio') as HTMLTextAreaElement;
        if (!nameEl || !bioEl) return false;
        const name = nameEl.value.trim();
        if (!name) {
          Swal.showValidationMessage('El nombre es obligatorio');
          return false;
        }
        return { name, bio: bioEl.value.trim() };
      },
    });

    if (result.isConfirmed && result.value) {
      const { name, bio } = result.value as { name: string; bio: string };
      setHosts((prev) => prev.map((h) => (h.id === host.id ? { ...h, name, bio } : h)));
      await SwalAlert.toast('Locutor actualizado.', 'success');
    }
  };

  const handleViewCommunity = (host: Host) => {
    Swal.fire({
      title: `Comunidad de ${host.name}`,
      html: `
        <div class="text-left space-y-3">
          <div class="grid grid-cols-2 gap-2">
            <div class="bg-background-100 rounded-xl p-3 text-center">
              <div class="w-8 h-8 mx-auto mb-1.5 rounded-lg bg-accent-50 flex items-center justify-center">
                <i class="ri-user-heart-line text-accent-500 text-sm"></i>
              </div>
              <p class="text-foreground-950 font-bold text-base">${host.followers >= 1000 ? `${(host.followers / 1000).toFixed(1)}K` : host.followers}</p>
              <p class="text-foreground-400 text-[9px]">Seguidores</p>
            </div>
            <div class="bg-background-100 rounded-xl p-3 text-center">
              <div class="w-8 h-8 mx-auto mb-1.5 rounded-lg bg-primary-50 flex items-center justify-center">
                <i class="ri-flashlight-line text-primary-500 text-sm"></i>
              </div>
              <p class="text-foreground-950 font-bold text-base">${host.totalInteractions >= 1000 ? `${(host.totalInteractions / 1000).toFixed(1)}K` : host.totalInteractions}</p>
              <p class="text-foreground-400 text-[9px]">Interacciones</p>
            </div>
          </div>
          <div class="bg-background-100 rounded-xl p-3 space-y-2">
            <p class="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Desglose de interacciones</p>
            <div class="flex items-center justify-between text-xs">
              <span class="text-foreground-500"><i class="ri-heart-line text-rose-400 mr-1"></i> Likes</span>
              <span class="text-foreground-700 font-bold">${host.interactions.likes.toLocaleString()}</span>
            </div>
            <div class="flex items-center justify-between text-xs">
              <span class="text-foreground-500"><i class="ri-chat-1-line text-sky-400 mr-1"></i> Comentarios</span>
              <span class="text-foreground-700 font-bold">${host.interactions.comments.toLocaleString()}</span>
            </div>
            <div class="flex items-center justify-between text-xs">
              <span class="text-foreground-500"><i class="ri-share-forward-line text-emerald-400 mr-1"></i> Compartidos</span>
              <span class="text-foreground-700 font-bold">${host.interactions.shares.toLocaleString()}</span>
            </div>
          </div>
          <p class="text-foreground-400 text-[9px]">Visitá la sección Comunidad para ver la actividad completa.</p>
        </div>
      `,
      showConfirmButton: true,
      confirmButtonText: 'Ir a Comunidad',
      showCancelButton: true,
      cancelButtonText: 'Cerrar',
      customClass: {
        popup: 'rounded-2xl border border-background-200/70 max-w-[420px]',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-primary-500 text-white hover:bg-primary-600',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-background-100 text-foreground-600 hover:bg-background-200',
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/admin/community');
      }
    });
  };

  const handleViewStats = (host: Host) => {
    const likesPct = host.totalInteractions > 0 ? ((host.interactions.likes / host.totalInteractions) * 100).toFixed(0) : '0';
    const commentsPct = host.totalInteractions > 0 ? ((host.interactions.comments / host.totalInteractions) * 100).toFixed(0) : '0';
    const sharesPct = host.totalInteractions > 0 ? ((host.interactions.shares / host.totalInteractions) * 100).toFixed(0) : '0';

    Swal.fire({
      title: `Estadísticas: ${host.name}`,
      html: `
        <div class="text-left space-y-3">
          <div class="grid grid-cols-3 gap-2">
            <div class="bg-background-100 rounded-xl p-3 text-center">
              <p class="text-foreground-400 text-[9px]">Seguidores</p>
              <p class="text-foreground-950 font-bold text-sm">${host.followers >= 1000 ? `${(host.followers / 1000).toFixed(1)}K` : host.followers}</p>
            </div>
            <div class="bg-background-100 rounded-xl p-3 text-center">
              <p class="text-foreground-400 text-[9px]">Total Interacc.</p>
              <p class="text-foreground-950 font-bold text-sm">${host.totalInteractions >= 1000 ? `${(host.totalInteractions / 1000).toFixed(1)}K` : host.totalInteractions}</p>
            </div>
            <div class="bg-background-100 rounded-xl p-3 text-center">
              <p class="text-foreground-400 text-[9px]">Programas</p>
              <p class="text-foreground-950 font-bold text-sm">${host.programs.length}</p>
            </div>
          </div>
          <div class="bg-background-100 rounded-xl p-3 space-y-2">
            <p class="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Distribución de interacciones</p>
            <div class="space-y-2">
              <div>
                <div class="flex items-center justify-between text-[11px] mb-1">
                  <span class="text-foreground-500">Likes</span>
                  <span class="text-foreground-700 font-semibold">${likesPct}%</span>
                </div>
                <div class="w-full h-2 rounded-full bg-background-200 overflow-hidden">
                  <div class="h-full rounded-full bg-rose-400" style="width:${likesPct}%"></div>
                </div>
              </div>
              <div>
                <div class="flex items-center justify-between text-[11px] mb-1">
                  <span class="text-foreground-500">Comentarios</span>
                  <span class="text-foreground-700 font-semibold">${commentsPct}%</span>
                </div>
                <div class="w-full h-2 rounded-full bg-background-200 overflow-hidden">
                  <div class="h-full rounded-full bg-sky-400" style="width:${commentsPct}%"></div>
                </div>
              </div>
              <div>
                <div class="flex items-center justify-between text-[11px] mb-1">
                  <span class="text-foreground-500">Compartidos</span>
                  <span class="text-foreground-700 font-semibold">${sharesPct}%</span>
                </div>
                <div class="w-full h-2 rounded-full bg-background-200 overflow-hidden">
                  <div class="h-full rounded-full bg-emerald-400" style="width:${sharesPct}%"></div>
                </div>
              </div>
            </div>
          </div>
          <p class="text-foreground-400 text-[9px]">Visitá Estadísticas para análisis detallado.</p>
        </div>
      `,
      showConfirmButton: true,
      confirmButtonText: 'Ir a Estadísticas',
      showCancelButton: true,
      cancelButtonText: 'Cerrar',
      customClass: {
        popup: 'rounded-2xl border border-background-200/70 max-w-[420px]',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-primary-500 text-white hover:bg-primary-600',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-background-100 text-foreground-600 hover:bg-background-200',
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/admin/analytics');
      }
    });
  };

  const handleViewRevenue = (host: Host) => {
    const growth = host.monthlyRevenue > 0 ? (((host.projectedRevenue - host.monthlyRevenue) / host.monthlyRevenue) * 100).toFixed(0) : '0';

    Swal.fire({
      title: `Ingresos: ${host.name}`,
      html: `
        <div class="text-left space-y-3">
          <div class="grid grid-cols-2 gap-2">
            <div class="bg-background-100 rounded-xl p-3 text-center">
              <p class="text-foreground-400 text-[9px]">Ingreso mensual</p>
              <p class="text-foreground-950 font-bold text-sm">₡${host.monthlyRevenue.toLocaleString()}</p>
            </div>
            <div class="bg-background-100 rounded-xl p-3 text-center">
              <p class="text-foreground-400 text-[9px]">Proyectado</p>
              <p class="text-foreground-950 font-bold text-sm">₡${host.projectedRevenue.toLocaleString()}</p>
            </div>
          </div>
          <div class="bg-emerald-50 rounded-xl p-3 border border-emerald-200">
            <div class="flex items-center gap-2">
              <div class="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                <i class="ri-line-chart-line text-emerald-600 text-sm"></i>
              </div>
              <div>
                <p class="text-emerald-700 font-bold text-sm">+${growth}% proyectado</p>
                <p class="text-emerald-500 text-[10px]">Crecimiento estimado próximo mes</p>
              </div>
            </div>
          </div>
          <div class="bg-background-100 rounded-xl p-3 space-y-2">
            <p class="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Fuentes de ingreso</p>
            <div class="flex items-center justify-between text-xs">
              <span class="text-foreground-500">Patrocinios de programa</span>
              <span class="text-foreground-700 font-bold">₡${Math.round(host.monthlyRevenue * 0.55).toLocaleString()}</span>
            </div>
            <div class="flex items-center justify-between text-xs">
              <span class="text-foreground-500">Menciones publicitarias</span>
              <span class="text-foreground-700 font-bold">₡${Math.round(host.monthlyRevenue * 0.30).toLocaleString()}</span>
            </div>
            <div class="flex items-center justify-between text-xs">
              <span class="text-foreground-500">Contenido exclusivo</span>
              <span class="text-foreground-700 font-bold">₡${Math.round(host.monthlyRevenue * 0.15).toLocaleString()}</span>
            </div>
          </div>
          <p class="text-foreground-400 text-[9px]">Visitá Monetización para el detalle completo de ingresos.</p>
        </div>
      `,
      showConfirmButton: true,
      confirmButtonText: 'Ir a Monetización',
      showCancelButton: true,
      cancelButtonText: 'Cerrar',
      customClass: {
        popup: 'rounded-2xl border border-background-200/70 max-w-[420px]',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-primary-500 text-white hover:bg-primary-600',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-background-100 text-foreground-600 hover:bg-background-200',
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/admin/monetization');
      }
    });
  };

  /* ─── LOADING ─── */
  if (dataState === 'loading') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-amber-500 flex items-center justify-center">
                <i className="ri-mic-line text-background-50 text-xs"></i>
              </div>
              <h1 className="text-foreground-950 font-bold text-sm">Locutores</h1>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center py-24">
            <div className="text-center">
              <div className="w-10 h-10 mx-auto mb-3 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
              <p className="text-foreground-500 text-sm">Cargando locutores…</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ─── ERROR ─── */
  if (dataState === 'error') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-amber-500 flex items-center justify-center">
                <i className="ri-mic-line text-background-50 text-xs"></i>
              </div>
              <h1 className="text-foreground-950 font-bold text-sm">Locutores</h1>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center py-24">
            <div className="text-center max-w-sm mx-auto px-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
                <i className="ri-error-warning-line text-red-500 text-2xl"></i>
              </div>
              <h3 className="text-foreground-950 font-bold text-base mb-2">Error al cargar</h3>
              <p className="text-foreground-500 text-sm mb-5">No se pudieron cargar los locutores.</p>
              <button onClick={retry} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer whitespace-nowrap">
                <i className="ri-refresh-line"></i>
                Reintentar
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ─── EMPTY ─── */
  if (dataState === 'empty') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-amber-500 flex items-center justify-center">
                <i className="ri-mic-line text-background-50 text-xs"></i>
              </div>
              <h1 className="text-foreground-950 font-bold text-sm">Locutores</h1>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center py-24">
            <div className="text-center max-w-sm mx-auto px-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
                <i className="ri-mic-off-line text-3xl text-foreground-400"></i>
              </div>
              <h3 className="text-foreground-950 font-bold text-base mb-2">Sin locutores</h3>
              <p className="text-foreground-500 text-sm mb-5">Todavía no hay locutores registrados. Agregá el primero desde Staff.</p>
              <Link to="/admin/staff" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer whitespace-nowrap">
                <i className="ri-team-line"></i>
                Ir a Staff
              </Link>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ─── READY ─── */
  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile header */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-amber-500 flex items-center justify-center">
              <i className="ri-mic-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Locutores</h1>
              <p className="text-foreground-400 text-[9px]">{counts.onAir} al aire</p>
            </div>
          </div>
          <Link to="/" className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors">
            <i className="ri-arrow-left-line text-xs"></i>
            <span className="hidden sm:inline">Volver al sitio</span>
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop header */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div>
              <h2 className="text-foreground-950 font-bold text-base">Locutores</h2>
              <p className="text-foreground-400 text-xs">
                {counts.total} locutores · {counts.onAir} al aire · {counts.active} activos · {counts.inactive} inactivos
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/admin/staff" className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer">
                <i className="ri-team-line text-xs"></i>
                Staff
              </Link>
              <Link to="/" className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer">
                <i className="ri-external-link-line text-xs"></i>
                Ver sitio
              </Link>
            </div>
          </div>

          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5">
            {/* KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <div className="bg-background-50 rounded-xl border border-background-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                    <i className="ri-mic-line text-amber-600 text-sm"></i>
                  </div>
                </div>
                <p className="text-foreground-950 text-lg font-bold">{counts.total}</p>
                <p className="text-foreground-400 text-[11px]">Total locutores</p>
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                    <i className="ri-broadcast-line text-red-600 text-sm"></i>
                  </div>
                </div>
                <p className="text-foreground-950 text-lg font-bold">{counts.onAir}</p>
                <p className="text-foreground-400 text-[11px]">Al aire</p>
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <i className="ri-check-line text-emerald-600 text-sm"></i>
                  </div>
                </div>
                <p className="text-foreground-950 text-lg font-bold">{counts.active}</p>
                <p className="text-foreground-400 text-[11px]">Activos</p>
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-foreground-100 flex items-center justify-center">
                    <i className="ri-pause-circle-line text-foreground-500 text-sm"></i>
                  </div>
                </div>
                <p className="text-foreground-950 text-lg font-bold">{counts.inactive}</p>
                <p className="text-foreground-400 text-[11px]">Inactivos</p>
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
                    <i className="ri-user-heart-line text-primary-600 text-sm"></i>
                  </div>
                </div>
                <p className="text-foreground-950 text-lg font-bold">{totalFollowers >= 1000 ? `${(totalFollowers / 1000).toFixed(1)}K` : totalFollowers}</p>
                <p className="text-foreground-400 text-[11px]">Seguidores</p>
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <i className="ri-money-dollar-circle-line text-emerald-600 text-sm"></i>
                  </div>
                </div>
                <p className="text-foreground-950 text-lg font-bold">₡{(totalRevenue / 1000000).toFixed(1)}M</p>
                <p className="text-foreground-400 text-[11px]">Ingreso proyectado</p>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="Buscar locutor o programa…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-foreground-800 text-sm outline-none focus:border-primary-400 transition-colors"
                />
                {search && (
                  <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-foreground-200 flex items-center justify-center cursor-pointer hover:bg-foreground-300 transition-colors">
                    <i className="ri-close-line text-foreground-500 text-[10px]"></i>
                  </button>
                )}
              </div>

              {/* Status pills */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <button
                  onClick={() => setSelectedStatus('todos')}
                  className={`px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                    selectedStatus === 'todos'
                      ? 'bg-foreground-800 text-background-50'
                      : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-100'
                  }`}
                >
                  Todos ({counts.total})
                </button>
                <button
                  onClick={() => setSelectedStatus('on_air')}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                    selectedStatus === 'on_air'
                      ? 'bg-red-500 text-white'
                      : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-100'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${selectedStatus === 'on_air' ? 'bg-white' : 'bg-red-500 animate-pulse'}`}></span>
                  Al aire ({counts.onAir})
                </button>
                <button
                  onClick={() => setSelectedStatus('active')}
                  className={`px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                    selectedStatus === 'active'
                      ? 'bg-emerald-500 text-white'
                      : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-100'
                  }`}
                >
                  Activos ({counts.active})
                </button>
                <button
                  onClick={() => setSelectedStatus('inactive')}
                  className={`px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                    selectedStatus === 'inactive'
                      ? 'bg-foreground-800 text-background-50'
                      : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-100'
                  }`}
                >
                  Inactivos ({counts.inactive})
                </button>
              </div>
            </div>

            {/* Empty search result */}
            {filteredHosts.length === 0 ? (
              <div className="bg-background-50 rounded-2xl border border-background-200 p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
                  <i className="ri-search-line text-2xl text-foreground-400"></i>
                </div>
                <h3 className="text-foreground-950 font-bold text-base mb-1">Sin resultados</h3>
                <p className="text-foreground-500 text-sm mb-4">Ningún locutor coincide con los filtros.</p>
                <button onClick={() => { setSearch(''); setSelectedStatus('todos'); }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-semibold hover:bg-background-200 cursor-pointer whitespace-nowrap">
                  <i className="ri-filter-off-line text-sm"></i>
                  Limpiar filtros
                </button>
              </div>
            ) : (
              /* Hosts grid */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredHosts.map((host) => (
                  <HostCard
                    key={host.id}
                    host={host}
                    onViewProfile={handleViewProfile}
                    onEdit={handleEdit}
                    onViewCommunity={handleViewCommunity}
                    onViewStats={handleViewStats}
                    onViewRevenue={handleViewRevenue}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>


    </div>
  );
}